import { IPortalAcesso } from '@/domain/portal/interfaces/portal';
import { portalRepository } from '@/domain/portal/repositories/portalRepository';
import { GatewayHttpError } from '@/infrastructure/http/gatewayFetch';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

/**
 * Whether the signed-in person is a brand buyer. Session data: the shell reads it to decide the
 * menu, the reachable routes and which shell loads may leave at all, so it loads once per
 * account (`usePortalAcessoBootstrap`) and is reset by the account switch like every store.
 */
interface PortalAcessoState {
  /** `null` until the first answer, so "staff" is never assumed before the API said so. */
  acesso: IPortalAcesso | null;
  loading: boolean;
  failed: boolean;
  /**
   * What the API said when it refused, so the wall that blocks the shell can repeat it.
   * "Nenhuma conta ativa na sua sessao. Escolha uma conta no menu do usuario." is an action
   * the person can take; "tente de novo em instantes" is a dead end for the same 403.
   */
  lastError: string;
  load: () => Promise<void>;
}

/**
 * The sentence the API itself answered, and only that one: a refusal explains what to do
 * ("choose an account in the user menu"), while a fetch that never got an answer would
 * put "Failed to fetch" on the wall. No answer keeps the screen's own wording.
 */
function apiMessage(error: unknown): string {
  if (!(error instanceof GatewayHttpError)) {
    return '';
  }
  return error.message.trim();
}

/** Only the latest request writes: an answer for the previous account arrives too late to count. */
let requestSeq = 0;

export const portalAcessoStore = createStore<PortalAcessoState>((set) => ({
  acesso: null,
  loading: false,
  failed: false,
  lastError: '',

  load: async () => {
    requestSeq += 1;
    const seq = requestSeq;
    set({ loading: true, failed: false, lastError: '' });
    try {
      const acesso = await portalRepository.getAcesso();
      if (seq !== requestSeq) {
        return;
      }
      set({
        acesso: {
          email: acesso?.email ?? '',
          clienteDaMarca: acesso?.clienteDaMarca === true,
          marcas: Array.isArray(acesso?.marcas) ? acesso.marcas : [],
        },
      });
    } catch (error) {
      logger.error('[portalAcessoStore] failed to load the portal access', error);
      if (seq === requestSeq) {
        set({ failed: true, lastError: apiMessage(error) });
      }
    } finally {
      if (seq === requestSeq) {
        set({ loading: false });
      }
    }
  },
}));
